import {schema} from '@/common/spec/spec-cache';

/**
 * Cache for all resolved entities within the API spec.
 * @type {{}}
 */
const resolveCache = {};

/**
 * List of special case keys used for the logic flow when parsing
 * the API spec.
 * @type {{allOf: string, customFields: string, description: string, embedded: string,
 * links: string, nested: string, objectDescription: string, properties: string, ref: string}}
 */
export const specialKeys = {
    allOf: 'allOf',
    array: 'array',
    arrayOfObjects: '$array',
    customFields: 'customFields',
    description: 'description',
    embedded: '$embedded',
    items: 'items',
    links: '_links',
    nested: '$nested',
    objectDescription: '$description',
    properties: 'properties',
    ref: '$ref',
};

/**
 * Defines the length of the prefix for
 * definition references.
 * Adjusted for OpenAPI3
 * @type {number}
 */
const defPrefixLength = '#/components/schemas/'.length;

/**
 * Resolve all entities, objects and references
 * within the properties provided, recursively.
 * @param properties {object}
 * @param resolve {Function}
 * @param nested {boolean} flag used to allow for exposing child nodes correctly
 */
function resolveRecursive(properties, resolve, nested = false) {
    const copiedProperties = {...properties};
    // prepare cache
    const definition = {};
    let index = 0;
    const keys = Object.keys(copiedProperties);
    const len = keys.length;
    for (; index < len; index += 1) {
        const key = keys[index];
        // we skip links from being processed
        if (key !== specialKeys.links) {
            // for custom fields we flag the node and continue
            // to the next one, post processing will probably
            // ensue outside of this
            if (key === specialKeys.customFields) {
                Reflect.set(definition, key, true);
            } else {
                // make a copy of the field details
                let def = {...copiedProperties[key]};
                // if we have an allOf then merge all
                // references into the definition
                if (Reflect.has(def, specialKeys.allOf)) {
                    const linked = def.allOf;
                    Reflect.deleteProperty(def, specialKeys.allOf);
                    let merged = {};
                    linked.forEach(({$ref = false}) => {
                        // TODO consider object resolve without ref, e.g. gateway credentials
                        if ($ref !== false) {
                            const linkedEntityName = $ref.substring(defPrefixLength);
                            const resolvedLink = resolve(linkedEntityName);
                            merged = {
                                ...merged,
                                ...resolvedLink,
                            };
                        }
                    });
                    // check if the `allOf` was the only thing present,
                    // and if so define a special key to differentiate
                    // nested objects from actual fields
                    if (!Reflect.has(merged, specialKeys.description)
                        || (Reflect.has(merged, specialKeys.description)
                            && typeof merged[specialKeys.description] !== 'string')) {
                        Reflect.set(merged, specialKeys.nested, true);
                    }
                    def = merged;
                } else if (Reflect.has(def, specialKeys.ref)) {
                    // in this case the node references another entity,
                    // we trigger another resolve on this node and pass
                    // a `nested: true` flag
                    const linkedEntityName = def[specialKeys.ref].substring(defPrefixLength);
                    def = resolve(linkedEntityName, true);
                    if (!nested) {
                        // this node isn't nested but it triggered
                        // the discovery of a child node, so we flag
                        // it as nested to prevent it showing up as an
                        // actual field
                        // ---
                        // check if the node is an array of objects,
                        // this is a particular case that needs special treatment
                        // e.g. contact emails and phone numbers
                        if (def.type
                            && def.type === specialKeys.array
                            && Reflect.has(def, specialKeys.items)
                            && Reflect.has(def[specialKeys.items], specialKeys.properties)) {
                            def = {
                                [specialKeys.nested]: true,
                                [specialKeys.arrayOfObjects]: true,
                                [specialKeys.objectDescription]: def.description,
                                // resolve item properties (array of objects)
                                ...resolveRecursive(def.items.properties, resolve, true),
                            };
                        } else {
                            Reflect.set(def, specialKeys.nested, true);
                        }
                    }
                } else if (Reflect.has(def, specialKeys.properties)) {
                    // otherwise if there's properties trigger a recursive
                    // discovery and flag this node as nested
                    def = {
                        [specialKeys.nested]: true,
                        [specialKeys.objectDescription]: def.description,
                        ...resolveRecursive(def.properties, resolve, true),
                    };
                }
                // save the definition for this key within the payload
                Reflect.set(definition, key, def);
            }
        }
    }
    return definition;
}

/**
 * Resolve an entity within the API spec and all its
 * properties recursively. Note: entity names are unique.
 * @param entityName {string}
 * @param nested (boolean} flag used for exposing child nodes correctly
 * @deprecated Use `SpecResolver.resolveEntity` instead
 * @returns {*}
 */
export default function resolveEntity(entityName, nested = false) {
    if (Reflect.has(resolveCache, entityName)) {
        return {...resolveCache[entityName]};
    }
    if (Reflect.has(schema, entityName)) {
        let definition = {...schema[entityName]};
        if (Reflect.has(definition, specialKeys.properties)) {
            definition = resolveRecursive(
                definition[specialKeys.properties],
                resolveEntity,
                nested,
            );
        }
        // cache resolved data
        Reflect.set(resolveCache, entityName, definition);
        return {...definition};
    }
    return null;
}
